import { Fragment, useRef, useState } from 'react';

import {
  OrganizationForm,
  OrganizationRegistrationNumberTooltip,
  type IOrganizationForm,
  type IOrganizationFormHandle,
} from '@/components/Organization';
import { CardContent, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useOnboardingStepSubmit, useStepSubmitHandler } from 'src/context/onboarding-step-submit';
import { type IEscrowPartyParams } from 'src/graphql/schema';

type Props = {
  defaultValues: IOrganizationForm;
  onSave: (params: IEscrowPartyParams) => Promise<boolean>;
  onCancel: () => void;
};

const InlineOrganizationForm = ({ defaultValues, onSave, onCancel }: Props) => {
  const formRef = useRef<IOrganizationFormHandle>(null);

  const saveValues = ({ companyRegistration, ...values }: IOrganizationForm) =>
    onSave({ ...values, companyRegistrationNumber: companyRegistration });

  const submitStep = async () => {
    const values = await formRef.current?.submit();

    if (!values) return 'Fix the highlighted fields to continue.';

    return saveValues(values);
  };

  useStepSubmitHandler(submitStep);

  const { clearStepError } = useOnboardingStepSubmit();
  const [isSaving, setIsSaving] = useState(false);

  const saveOrganization = async () => {
    const values = await formRef.current?.submit();

    if (!values) return;

    setIsSaving(true);

    try {
      if (await saveValues(values)) clearStepError();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Fragment>
      <CardContent>
        <OrganizationForm
          ref={formRef}
          onSubmit={saveValues}
          defaultValues={defaultValues}
          registrationTooltip={<OrganizationRegistrationNumberTooltip />}
          hideActions
        />
      </CardContent>
      <CardFooter className="bg-muted py-3 justify-end gap-2 rounded-b-xl">
        <Button variant="secondary" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button size="sm" onClick={saveOrganization} isLoading={isSaving}>
          Save
        </Button>
      </CardFooter>
    </Fragment>
  );
};

export default InlineOrganizationForm;
